import React, { Fragment, useState, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { useAlert } from "react-alert";
import apiClient from "../../../../utils/apiClient";

const ImportStudents = () => {
  const alert = useAlert();
  const history = useHistory();

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sendEmails, setSendEmails] = useState(true);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  // Process file and convert to base64
  const processFile = (selectedFile) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const extension = selectedFile.name.split('.').pop().toLowerCase();
    const validExtensions = ['csv', 'xls', 'xlsx'];
    
    if (!validExtensions.includes(extension)) {
      alert.error("Please select a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    setFileName(selectedFile.name);
    setFileType(extension);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      // Convert to base64
      const base64 = e.target.result.split(',')[1];
      setFile(base64);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  }, []);

  // Download template
  const downloadTemplate = async (format = 'csv') => {
    try {
      const response = await apiClient.get(`/admin/import-template?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `student_import_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert.error("Failed to download template");
    }
  };

  // Submit import
  const handleImport = async () => {
    if (!file) {
      alert.error("Please select a file first");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const { data } = await apiClient.post('/admin/import-students', {
        fileContent: file,
        fileType: fileType,
        sendEmails: sendEmails
      });

      if (data.success) {
        setResults(data.data);
        alert.success(data.message);
      } else {
        alert.error(data.message || "Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      alert.error(error.response?.data?.message || "Import failed. Please check your file format.");
    } finally {
      setIsLoading(false);
    }
  };

  // Clear selection
  const handleClear = () => {
    setFile(null);
    setFileName("");
    setFileType("");
    setResults(null);
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Import <small>Students</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin/dashboard">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li>
              <Link to="/admin/students">Students</Link>
            </li>
            <li className="active">Import</li>
          </ol>
        </section>

        <section className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="box box-primary">
                <div className="box-header with-border">
                  <h3 className="box-title">Import Students from File</h3>
                </div>
                <div className="box-body">
                  {/* Instructions */}
                  <div className="alert alert-info">
                    <h4><i className="icon fa fa-info"></i> Instructions</h4>
                    <ul className="mb-0">
                      <li>Upload a CSV or Excel file with student information</li>
                      <li>Required column: <strong>email</strong></li>
                      <li>Optional columns: first_name, last_name, phone</li>
                      <li>A temporary password will be generated and sent to each student</li>
                    </ul>
                    <div className="mt-3">
                      <button 
                        type="button" 
                        className="btn btn-default btn-sm mr-2"
                        onClick={() => downloadTemplate('csv')}
                      >
                        <i className="fa fa-download"></i> Download CSV Template
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-default btn-sm"
                        onClick={() => downloadTemplate('xlsx')}
                        style={{ marginLeft: '10px' }}
                      >
                        <i className="fa fa-download"></i> Download Excel Template
                      </button>
                    </div>
                  </div>

                  {/* File Upload Area */}
                  <div 
                    className={`dropzone-area ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    style={{
                      border: '2px dashed #ccc',
                      borderRadius: '8px',
                      padding: '40px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      marginBottom: '20px',
                      backgroundColor: dragActive ? '#e3f2fd' : '#fafafa',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <input
                      type="file"
                      id="fileInput"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="fileInput" style={{ cursor: 'pointer', width: '100%' }}>
                      {fileName ? (
                        <div>
                          <i className="fa fa-file-excel-o fa-3x text-success mb-3"></i>
                          <p className="mb-0"><strong>{fileName}</strong></p>
                          <p className="text-muted">Click or drag to replace</p>
                        </div>
                      ) : (
                        <div>
                          <i className="fa fa-cloud-upload fa-3x text-muted mb-3"></i>
                          <p><strong>Drag & drop your file here</strong></p>
                          <p className="text-muted">or click to browse</p>
                          <p className="text-muted small">Supports: CSV, XLS, XLSX</p>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Options */}
                  <div className="form-group">
                    <div className="checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={sendEmails}
                          onChange={(e) => setSendEmails(e.target.checked)}
                        />
                        <strong> Send temporary passwords via email</strong>
                        <p className="text-muted small mb-0">
                          Each imported student will receive an email with their login credentials
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleImport}
                      disabled={!file || isLoading}
                    >
                      {isLoading ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Importing...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-upload"></i> Import Students
                        </>
                      )}
                    </button>
                    {file && (
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={handleClear}
                        style={{ marginLeft: '10px' }}
                      >
                        <i className="fa fa-times"></i> Clear
                      </button>
                    )}
                    <Link to="/admin/students" className="btn btn-default" style={{ marginLeft: '10px' }}>
                      <i className="fa fa-arrow-left"></i> Back to Students
                    </Link>
                  </div>

                  {/* Results */}
                  {results && (
                    <div className="mt-4">
                      <div className="box box-solid">
                        <div className="box-header with-border">
                          <h4 className="box-title">Import Results</h4>
                        </div>
                        <div className="box-body">
                          <div className="row">
                            <div className="col-md-3">
                              <div className="info-box bg-aqua">
                                <span className="info-box-icon"><i className="fa fa-users"></i></span>
                                <div className="info-box-content">
                                  <span className="info-box-text">Total Records</span>
                                  <span className="info-box-number">{results.total}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="info-box bg-green">
                                <span className="info-box-icon"><i className="fa fa-check"></i></span>
                                <div className="info-box-content">
                                  <span className="info-box-text">Created</span>
                                  <span className="info-box-number">{results.created}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="info-box bg-yellow">
                                <span className="info-box-icon"><i className="fa fa-forward"></i></span>
                                <div className="info-box-content">
                                  <span className="info-box-text">Skipped</span>
                                  <span className="info-box-number">{results.skipped}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="info-box bg-red">
                                <span className="info-box-icon"><i className="fa fa-exclamation-triangle"></i></span>
                                <div className="info-box-content">
                                  <span className="info-box-text">Errors</span>
                                  <span className="info-box-number">{results.errors?.length || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Created Users */}
                          {results.createdUsers && results.createdUsers.length > 0 && (
                            <div className="mt-3">
                              <h5><i className="fa fa-check-circle text-success"></i> Created Users:</h5>
                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Name</th>
                                      <th>Email</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {results.createdUsers.map((user, index) => (
                                      <tr key={user.id}>
                                        <td>{index + 1}</td>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Errors */}
                          {results.errors && results.errors.length > 0 && (
                            <div className="mt-3">
                              <h5><i className="fa fa-exclamation-circle text-danger"></i> Errors/Skipped:</h5>
                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead>
                                    <tr>
                                      <th>Email</th>
                                      <th>Reason</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {results.errors.map((err, index) => (
                                      <tr key={index}>
                                        <td>{err.email}</td>
                                        <td>{err.reason}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ImportStudents;
